"use client";

import { useCallback, useEffect, useRef, type RefObject } from "react";

/** Consecutive pages that may arrive without changing the list before it stops asking. */
const MAX_NO_PROGRESS_PAGES = 3;

interface FetchNextPageOnReachOptions {
  hasNextPage: boolean;
  /**
   * React Query's own flag. Never a local latch fed by fetchNextPage()'s
   * promise: a search/sort/facet change aborts the in-flight fetch and that
   * promise never settles, so a latch would stay stuck true and kill infinite
   * scroll for the rest of the session.
   */
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  /**
   * How close the sentinel gets to the bottom of the screen before fetching,
   * in viewport heights. Viewport-relative rather than px so the runway grows
   * with the screen instead of swallowing a short first page on small ones —
   * a fixed px lookahead tuned for desktop fires instantly on devices where
   * one page is barely taller than it.
   */
  marginScreens: number;
  /**
   * The rows this list currently renders, by identity. Used to require progress
   * between requests: at the end of a list the sentinel settles at a fixed
   * screen position, so geometry alone cannot tell "already asked for this" from
   * "reached the end again after a page arrived".
   */
  rows: readonly unknown[] | undefined;
}

/**
 * Loads the next page once a sentinel element sitting at the END of a list comes
 * within `marginScreens` viewport heights of the bottom of the screen.
 *
 * Screen space, not the virtualizer's window: `overscan` is tuned for rendering
 * ahead, not for prefetch distance, so in a short container it spans the whole
 * first page and would fetch page 2 before the user has scrolled at all.
 *
 * Checked on every commit AND on capture-phase scroll, because these lists are
 * not always scrolled by their own container — several pages scroll an ancestor
 * instead, and then nothing re-renders the list. Checking only on render stalls
 * infinite scroll in those layouts; checking only the list's own scrollTop
 * misses them too.
 */
export function useFetchNextPageOnReach(
  sentinelRef: RefObject<HTMLElement | null>,
  options: FetchNextPageOnReachOptions
) {
  // Latest props for the listeners below, which attach once.
  const latest = useRef(options);
  // The rows this last asked for a page at. Identified by first row + count,
  // NOT by array identity: a response that appends nothing still produces a
  // fresh pages array from React Query and a fresh rows array from the list's
  // render-phase adoption, so identity alone always looks like a change and
  // would let the guard below fetch again on every commit.
  const lastRequest = useRef<{
    first: unknown;
    length: number;
  } | null>(null);
  // Pages asked for in a row that changed nothing — see the guard below.
  const noProgress = useRef(0);
  // Whether a next-page fetch this hook asked for has come back since the last
  // one it asked for. Distinguishes "a page arrived and added nothing" from
  // "the query was replaced under us" — see the guard below.
  const pageLanded = useRef(false);

  const check = useCallback(() => {
    const {
      hasNextPage,
      isFetchingNextPage,
      fetchNextPage,
      marginScreens,
      rows,
    } = latest.current;
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage || isFetchingNextPage) return;
    // A list that isn't rendered must never drive fetching: a display:none copy
    // of the same table (the city pages mount one) reports every rect at 0,
    // which reads as "the end is on screen" forever.
    if (sentinel.getClientRects().length === 0 || !sentinel.offsetParent)
      return;
    const end = sentinel.getBoundingClientRect().top;
    if (end > window.innerHeight * (1 + marginScreens)) return;
    // Never ask twice from the same rows. A page that arrives without changing
    // THIS list — a hidden second instance, a response with no new rows —
    // otherwise leaves the geometry satisfied and asks for another page on
    // every commit, forever. Any real change clears it: a page appended grows
    // the count, and a re-keyed search replaces the first row once its own page
    // lands.
    //
    // Deliberately NOT also keyed on the sentinel's position: while a re-keyed
    // search fetches page 1, placeholderData still reports the OLD rows and
    // hasNextPage: true, so any scroll of a few px would look like a new
    // position and fire fetchNextPage() against a query with nothing cached —
    // which cancels and re-issues that page-1 request on every keystroke.
    // Cost of leaving position out: a fetch that fails outright is not retried
    // by scrolling, only by React Query's own retries.
    //
    // Not an absolute stop, because a page CAN legitimately arrive and change
    // nothing: the trade views run dedupeByKey over the accumulated rows, so a
    // page whose items are all already loaded is dropped whole. Walking past a
    // few of those is what keeps such a list scrollable. A commit while the
    // fetch is in flight returns above, so this counts pages, not renders.
    // ponytail: fixed cap, not a page-number check — the callers are generic
    // components that never see the query's page count. Thread one through if
    // a list ever needs to skip more than this many duplicate pages in a row.
    //
    // And only a page WE asked for buys an allowance. Unchanged rows have a
    // second cause: a re-key (a search keystroke, a facet) while the user sits
    // at the end of the list. The new query holds nothing but placeholderData,
    // so its rows are still the old ones and its hasNextPage is still true —
    // and every fetchNextPage() against a query with no cached pages makes
    // React Query re-issue page 1 with cancelRefetch, aborting the request
    // already in flight. That path never sets isFetchingNextPage, so requiring
    // a completed next-page fetch tells the two apart with the flag React Query
    // already gives us.
    const previous = lastRequest.current;
    const first = rows?.[0];
    const length = rows?.length ?? 0;
    if (previous && previous.first === first && previous.length === length) {
      if (!pageLanded.current || noProgress.current >= MAX_NO_PROGRESS_PAGES)
        return;
      noProgress.current += 1;
    } else {
      noProgress.current = 0;
    }
    pageLanded.current = false;
    lastRequest.current = { first, length };
    fetchNextPage();
  }, [sentinelRef]);

  // No dependency array: the geometry decides, so re-checking after every commit
  // is what covers mount, an arriving page and virtualizer updates.
  useEffect(() => {
    if (latest.current.isFetchingNextPage && !options.isFetchingNextPage) {
      pageLanded.current = true;
    }
    latest.current = options;
    check();
  });

  useEffect(() => {
    const onScroll = () => check();
    // Capture phase so this fires for a scroll on ANY ancestor, not just window.
    window.addEventListener("scroll", onScroll, {
      capture: true,
      passive: true,
    });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll, { capture: true });
      window.removeEventListener("resize", onScroll);
    };
  }, [check]);
}
